import { signUp, resendSignUpCode, confirmSignUp, signIn, confirmResetPassword } from "aws-amplify/auth";
import { request } from "./request";

export interface LoginChallengeRequest {
  readonly identity: string;
}

export interface LoginChallengeResponse {
  readonly redirect?: string;
  readonly authenticator?: "aws/cognito" | "capitalrx/sts";
}

export const challengeLogin = async (body: LoginChallengeRequest) => {
  // @TODO -- analytics, maybe?
  return request<LoginChallengeRequest, LoginChallengeResponse>({
    endpoint: "/login/challenge",
    body: { ...body },
    api: "authnz",
  });
};

export interface CognitoLoginRequest {
  readonly identity: string;
  readonly password: string;
}

export const cognitoLogin = async (body: CognitoLoginRequest) => {
  return signIn({
    username: body.identity.trim().toLowerCase(),
    password: body.password,
  });
};

interface AmplifySignUp {
  readonly email: string;
  readonly password: string;
  readonly phone_number?: string;
  readonly member: {
    readonly id: number;
    readonly external_member_id: string;
    readonly person_code: string;
    readonly first_name: string;
    readonly last_name: string;
  };
  readonly plan: {
    readonly id: number;
    readonly name: string;
    readonly client_id: string;
    readonly bin_number: string;
    readonly processor_control_number: string;
    readonly plan_type: string;
  };
}

export const cognitoRegisterUser = async (registration: AmplifySignUp) => {
  const { email, password, member, plan } = registration;
  const userType = plan.plan_type === "discount_card" ? "discount_card" : "commercial";

  return signUp({
    username: email.trim().toLowerCase(),
    password: password,
    options: {
      userAttributes: {
        "custom:member_id": member.id.toString(),
        name: `${member.first_name} ${member.last_name}`,
        email: email.trim().toLowerCase(),
        "custom:user_type": userType,
      },
    },
  });
};

export const cognitoResentSignUpCode = async (email: string) => {
  return resendSignUpCode({
    username: email,
  });
};

export const cognitoConfirmRegistrationCode = async (email: string, code: string) => {
  return confirmSignUp({
    username: email,
    confirmationCode: code,
  });
};

export interface UpdatePasswordRequest {
  readonly identity: string;
  readonly confirmation_code: string;
  readonly password: string;
}

export const cognitoUpdatePassword = async (body: UpdatePasswordRequest) => {
  await confirmResetPassword({
    username: body.identity,
    confirmationCode: body.confirmation_code,
    newPassword: body.password,
  });
};

export interface GenerateForgotPasswordEmailRequest {
  readonly identity: string;
  readonly external_member_id: string;
}

export const generateForgotPasswordEmail = async (body: GenerateForgotPasswordEmailRequest) => {
  return request<{ readonly identity: string }, never>({
    endpoint: "/user/forgot_password/generate_email",
    body: { ...body },
    api: "authnz",
  });
};

export const migrateNotification = async (identity: string) => {
  return request<{ readonly identity: string }, never>({
    endpoint: "/user/notification_settings/migrate",
    body: { identity },
    api: "authnz",
  });
};

export interface PreRegisterUserRequest {
  readonly first_name: string;
  readonly last_name: string;
  readonly date_of_birth: string;
  readonly external_member_id?: string;
  readonly social_security_number?: string;
  readonly post_auth?: string;
}

export interface PreRegisterUserUnprocessable {
  readonly status?: string;
  readonly message?: string;
  readonly message_url?: string;
  readonly saml_redirect?: string;
  readonly sp_disabled: boolean;
  readonly allow_optional: boolean;
  readonly identity_provider: string;
}

export const preRegisterUser = async (body: PreRegisterUserRequest) => {
  return request<PreRegisterUserRequest, never>({
    endpoint: "/user/preregister",
    body: { ...body },
    api: "authnz",
  });
};

export interface RegisterUserRequest {
  readonly authenticator?: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly date_of_birth: string;
  readonly external_member_id?: string;
  readonly social_security_number?: string;
  readonly opt_in_to_emails: boolean;
  readonly email: string;
}

export interface RegisterUserResponse {
  readonly member: {
    readonly id: number;
    readonly external_member_id: string;
    readonly person_code: string;
    readonly first_name: string;
    readonly last_name: string;
  };
  readonly plan: {
    readonly id: number;
    readonly name: string;
    readonly client_id: string;
    readonly bin_number: string;
    readonly processor_control_number: string;
    readonly plan_type: string;
  };
}

export const registerUser = async (body: RegisterUserRequest) => {
  return request<PreRegisterUserRequest, RegisterUserResponse>({
    endpoint: "/user/register_v2",
    body: { ...body },
    api: "authnz",
  });
};

export interface AddNativeRegistrationRequest {
  readonly email: string;
}

export interface AddNativeRegistrationResponse {
  readonly member: {
    readonly id: number;
    readonly external_member_id: string;
    readonly person_code: string;
    readonly first_name: string;
    readonly last_name: string;
  };
  readonly plan: {
    readonly id: number;
    readonly name: string;
    readonly client_id: string;
    readonly bin_number: string;
    readonly processor_control_number: string;
    readonly plan_type: string;
  };
}

export const addNativeRegistration = async (authToken: string, body: AddNativeRegistrationRequest) => {
  return request<AddNativeRegistrationRequest, AddNativeRegistrationResponse>({
    endpoint: "/user/add_native_registration",
    body: { ...body },
  }, authToken);
};

export interface ValidateForgotPasswordTokenRequest {
  readonly identity?: string;
  readonly token?: string;
}

export interface ValidateForgotPasswordTokenResponse {
  readonly identity?: string;
}

export const validateForgotPasswordToken = async (body: ValidateForgotPasswordTokenRequest) => {
  return request<ValidateForgotPasswordTokenRequest, ValidateForgotPasswordTokenResponse>({
    endpoint: "/user/forgot_password/validate_token",
    body: { ...body },
    api: "authnz",
  });
};

export interface ChallengeMFAResponse {
  readonly force_mfa: boolean;
}

export const challengeMfa = async (authToken: string) => {
  return request<{}, ChallengeMFAResponse>({
    endpoint: "/user/challenge_mfa",
    body: {},
  }, authToken);
};
